import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const { t } = useTranslation();
  const { brand, reputation, contact } = aboutData;

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '麻豆传媒',
    url: 'https://madouapp.com/',
    description: brand.description,
    email: contact.email,
    telephone: contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '科技园南路88号麻豆传媒大厦',
      addressLocality: '深圳',
      addressRegion: '广东',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '麻豆传媒是什么公司？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: brand.description,
        },
      },
      {
        '@type': 'Question',
        name: '如何联系麻豆传媒客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以通过以下方式联系我们：邮箱 ${contact.email}，电话 ${contact.phone}，工作时间 ${contact.workingHours}。`,
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Page Header */}
      <section className="relative w-full h-[260px] md:h-[340px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20corporate%20office%20building%20exterior%20with%20warm%20lighting%20at%20sunset%2C%20clean%20minimalist%20architecture%2C%20glass%20facade%2C%20professional%20atmosphere%2C%20artistic%20composition%2C%20premium%20quality&width=1920&height=600&seq=about-hero&orientation=landscape"
            alt="关于麻豆传媒"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/20"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 pb-8 md:pb-12">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-white font-heading">
              {t('about_title')}
            </h1>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="w-full px-4 md:px-6 pt-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: t('breadcrumb_home'), path: '/' },
              { label: t('about_title') },
            ]}
          />
        </div>
      </div>

      {/* Brand Introduction */}
      <section className="py-12 md:py-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="lg:w-1/2">
              <span className="inline-block text-xs font-semibold text-primary-600 bg-primary-100 px-3 py-1 rounded-full mb-4">
                {t('about_brand_title')}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-6">
                用科技连接创作者与用户
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                {brand.description}
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                <strong className="text-foreground-900">我们的使命：</strong>{brand.mission}
              </p>
              <p className="text-foreground-600 leading-relaxed">
                <strong className="text-foreground-900">我们的愿景：</strong>{brand.vision}
              </p>
            </div>
            <div className="lg:w-1/2">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20open%20plan%20tech%20office%20interior%20with%20warm%20natural%20lighting%2C%20collaborative%20workspace%2C%20minimalist%20design%2C%20plants%20and%20wood%20accents%2C%20professional%20creative%20atmosphere%2C%20high%20quality%20interior%20photography&width=1200&height=800&seq=about-office&orientation=landscape"
                  alt="麻豆传媒办公室"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              发展历程
            </h2>
            <p className="text-foreground-500 text-sm md:text-base">
              从2018年成立至今，我们不断成长
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 transform md:-translate-x-px"></div>
            <div className="space-y-8">
              {brand.milestones.map((ms, index) => (
                <div
                  key={ms.year}
                  className={`relative flex items-start gap-6 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 transform -translate-x-1/2 mt-1.5 z-10"></div>
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'} pl-10 md:pl-0`}>
                    <div className="bg-background-50 rounded-xl p-5 border border-background-200/70 inline-block">
                      <span className="text-lg font-bold text-primary-600 font-heading">
                        {ms.year}
                      </span>
                      <p className="text-foreground-600 text-sm mt-1">{ms.event}</p>
                    </div>
                  </div>
                  {index % 2 === 0 ? <div className="hidden md:block md:w-1/2"></div> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section className="py-12 md:py-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-semibold text-accent-700 bg-accent-100 px-3 py-1 rounded-full mb-4">
              {t('about_reputation_title')}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              {reputation.title}
            </h2>
            <p className="text-foreground-500 text-sm md:text-base">
              {reputation.subtitle}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
            {reputation.stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70"
              >
                <p className="text-2xl md:text-3xl font-bold text-accent-600 font-heading mb-1">
                  {stat.value}
                </p>
                <p className="text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Reviews */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {reputation.reviews.map((review, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-xl p-6 border border-background-200/70"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden shrink-0">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{review.name}</p>
                    <p className="text-xs text-foreground-500">{review.role}</p>
                    <div className="flex items-center gap-0.5 mt-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${
                            i < review.rating
                              ? 'ri-star-fill text-accent-500'
                              : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed">{review.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-semibold text-secondary-700 bg-secondary-100 px-3 py-1 rounded-full mb-4">
              {t('about_contact_title')}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              {contact.title}
            </h2>
            <p className="text-foreground-500 text-sm md:text-base">
              {contact.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
            <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-mail-line text-primary-600 text-xl"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-2">电子邮箱</h3>
              <a
                href={`mailto:${contact.email}`}
                className="text-sm text-primary-600 hover:underline cursor-pointer"
              >
                {contact.email}
              </a>
            </div>
            <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-phone-line text-accent-600 text-xl"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-2">客服电话</h3>
              <a
                href={`tel:${contact.phone}`}
                className="text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {contact.phone}
              </a>
            </div>
            <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-map-pin-line text-secondary-600 text-xl"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-2">公司地址</h3>
              <p className="text-sm text-foreground-600">{contact.address}</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-foreground-500 mb-4">
              工作时间：{contact.workingHours}
            </p>
            <div className="flex items-center justify-center gap-4">
              {contact.socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  className="w-10 h-10 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:border-primary-300 transition-colors cursor-pointer"
                  aria-label={link.name}
                  rel="nofollow"
                >
                  <i className={`${link.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}